import React from "react";
import PrintableSheet from "./PrintableSheet";
import { CheckSquare, AlertTriangle, Wrench, Phone } from "lucide-react";
import { colors } from "../../config/site";

const MONTHLY = [
  "Test smoke and carbon monoxide detectors",
  "Replace HVAC air filters (every 1–3 months)",
  "Run hot water in unused drains for 1 minute",
  "Wipe down range hood filter",
  "Check under sinks for leaks or water marks",
  "Clean lint trap behind dryer (not just the screen)",
  "Vacuum refrigerator coils once a quarter",
];

const SPRING = [
  "Check window screens for tears; clean window tracks",
  "Inspect caulking around tubs, showers, and sinks — re-caulk if cracked",
  "Vacuum and rotate mattresses",
  "Wash bedding, curtains, and shower curtain liners",
  "Clean refrigerator coils and behind appliances",
  "Test all GFCI outlets (the ones near water)",
  "Check the dryer vent hose for lint build-up",
];

const SUMMER = [
  "Clean window AC unit filters and front panels",
  "Inspect doors and windows for drafts; re-weather-strip if needed",
  "Wipe down ceiling fan blades and reverse direction (counter-clockwise = cool)",
  "Deep-clean carpets or have them shampooed",
  "Inspect for any signs of pests (ants, roaches, mice)",
  "Wash baseboards and clean light fixtures / lampshades",
];

const FALL = [
  "Have heating system inspected before first cold night",
  "Replace HVAC / furnace filter",
  "Test smoke and CO detectors — replace batteries",
  "Reverse ceiling fans (clockwise = warm)",
  "Check window and door seals before winter",
  "Drain outdoor hoses; cover outdoor faucets",
  "Clean gutters (if applicable — landlord may handle)",
];

const WINTER = [
  "Open cabinets under sinks during cold nights to prevent frozen pipes",
  "Keep heat at 55°F or higher even when away to prevent pipe freeze",
  "Run a trickle of water on extra cold nights from one tap",
  "Wipe condensation off windows to prevent mold",
  "Inspect for ice dams on roof edges (report to landlord)",
  "Check for drafts and add door snakes where needed",
];

const DIY = [
  { task: "Light bulbs", who: "You" },
  { task: "Air filter changes", who: "You" },
  { task: "Battery replacements (smoke/CO detectors)", who: "You" },
  { task: "Unclog minor sink/tub drains (plunger or boiling water)", who: "You" },
  { task: "Reset a tripped breaker", who: "You" },
  { task: "Clean lint traps", who: "You" },
  { task: "Tighten loose cabinet pulls or door knobs", who: "You" },
  { task: "Replace shower curtain liner", who: "You" },
];

const LANDLORD = [
  { task: "Major leaks, flooding, or burst pipes", who: "Landlord" },
  { task: "No heat, no hot water, no electricity", who: "Landlord (emergency)" },
  { task: "Gas smell or carbon monoxide alarm", who: "911 first, then landlord" },
  { task: "Mold or major water damage", who: "Landlord" },
  { task: "Pest infestation (roaches, rodents, bedbugs)", who: "Landlord" },
  { task: "Broken windows, doors, or locks", who: "Landlord" },
  { task: "Appliance breakdowns (fridge, stove, washer)", who: "Landlord" },
  { task: "Anything affecting health or safety", who: "Landlord" },
];

const EMERGENCY_CONTACTS = [
  { label: "All Emergencies (fire, medical, police)", value: "911" },
  { label: "Philadelphia Gas Works — Gas Leak", value: "215-235-1212" },
  { label: "PECO — Power Outage / Downed Line", value: "1-800-841-4141" },
  { label: "Philadelphia Water Dept. Emergencies", value: "215-685-6300" },
  { label: "Poison Control", value: "1-800-222-1222" },
  { label: "Philadelphia 311 (non-emergency)", value: "311" },
];

const Card = ({ title, accent, items, icon: Icon, testId }) => (
  <section
    className="rounded-2xl p-5 print:p-4 print:break-inside-avoid"
    style={{ backgroundColor: colors.ivory, border: `1px solid ${accent}33` }}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-3">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: accent + "22", color: accent }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="text-lg font-bold" style={{ color: colors.charcoal }}>
        {title}
      </h3>
    </div>
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-start gap-2 text-sm"
          style={{ color: colors.charcoal }}
        >
          <CheckSquare
            className="w-4 h-4 mt-0.5 flex-shrink-0"
            style={{ color: accent }}
            strokeWidth={2}
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </section>
);

const MaintenanceGuide = () => {
  return (
    <PrintableSheet
      eyebrow="RENTERS · HOME MAINTENANCE GUIDE"
      title="Year-Round Home Maintenance Guide"
      subtitle="Simple, practical maintenance tasks every renter should know — to keep your home safe, healthy, and in good shape."
      backHref="/resources/renters"
      backLabel="Back to Renter Resources"
      testId="maintenance-guide-page"
    >
      <div className="space-y-8 print:space-y-6">
        {/* 1. Monthly Quick-Check */}
        <section className="print:break-inside-avoid">
          <h2
            className="text-2xl font-bold mb-3"
            style={{ color: colors.charcoal }}
            data-testid="maint-section-monthly"
          >
            1 · Monthly Quick-Check List
          </h2>
          <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
            Set a recurring monthly reminder. The whole list takes about 15–20 minutes.
          </p>
          <div
            className="rounded-2xl p-5"
            style={{ backgroundColor: colors.cream, border: `2px solid ${colors.sage}55` }}
          >
            <ul className="grid sm:grid-cols-2 gap-2">
              {MONTHLY.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm"
                  style={{ color: colors.charcoal }}
                >
                  <CheckSquare
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                    style={{ color: colors.sage }}
                    strokeWidth={2}
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 2. Seasonal */}
        <section>
          <h2 className="text-2xl font-bold mb-3" style={{ color: colors.charcoal }}>
            2 · Seasonal Checklists
          </h2>
          <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
            Do these once at the start of each season.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Card title="Spring" accent={colors.sage} items={SPRING} icon={Wrench} testId="maint-spring" />
            <Card title="Summer" accent={colors.gold} items={SUMMER} icon={Wrench} testId="maint-summer" />
            <Card title="Fall" accent={colors.terracotta} items={FALL} icon={Wrench} testId="maint-fall" />
            <Card title="Winter" accent={colors.primary} items={WINTER} icon={Wrench} testId="maint-winter" />
          </div>
        </section>

        {/* 3. Fix yourself vs. landlord */}
        <section>
          <h2 className="text-2xl font-bold mb-3" style={{ color: colors.charcoal }}>
            3 · Fix It Yourself vs. Call the Landlord
          </h2>
          <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
            When in doubt, call the landlord — but here&apos;s a starting guide.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div
              className="rounded-2xl p-5"
              style={{ backgroundColor: colors.sage + "15", border: `1px solid ${colors.sage}55` }}
            >
              <h3 className="text-lg font-bold mb-3" style={{ color: colors.primary }}>
                You Can Usually Handle
              </h3>
              <ul className="space-y-2">
                {DIY.map((row, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm"
                    style={{ color: colors.charcoal }}
                  >
                    <CheckSquare
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      style={{ color: colors.sage }}
                    />
                    <span>{row.task}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className="rounded-2xl p-5"
              style={{
                backgroundColor: colors.terracotta + "12",
                border: `1px solid ${colors.terracotta}55`,
              }}
            >
              <h3 className="text-lg font-bold mb-3" style={{ color: colors.terracottaDark }}>
                Always Call the Landlord
              </h3>
              <ul className="space-y-2">
                {LANDLORD.map((row, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm"
                    style={{ color: colors.charcoal }}
                  >
                    <AlertTriangle
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      style={{ color: colors.terracotta }}
                    />
                    <span>
                      {row.task}{" "}
                      <span
                        className="ml-1 text-xs font-semibold"
                        style={{ color: colors.terracottaDark }}
                      >
                        ({row.who})
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p
            className="text-xs mt-3 italic"
            style={{ color: colors.textMuted }}
          >
            Always put maintenance requests in writing (text or email) so you have a record.
          </p>
        </section>

        {/* 4. Emergency contacts */}
        <section className="print:break-inside-avoid">
          <h2 className="text-2xl font-bold mb-3" style={{ color: colors.charcoal }}>
            4 · Emergency Contacts &amp; Shutoffs
          </h2>
          <div
            className="rounded-2xl p-5"
            style={{ backgroundColor: colors.primary, color: colors.white }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Phone className="w-5 h-5" style={{ color: colors.gold }} />
              <h3 className="text-lg font-bold">Keep these numbers somewhere visible</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {EMERGENCY_CONTACTS.map((c) => (
                <div
                  key={c.label}
                  className="flex justify-between gap-3 py-2 border-b border-white/20"
                >
                  <span className="text-sm opacity-90">{c.label}</span>
                  <span className="text-sm font-bold whitespace-nowrap">{c.value}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-4 border-t border-white/30">
              <p
                className="text-xs uppercase tracking-widest font-bold mb-3"
                style={{ color: colors.gold }}
              >
                Know Your Shutoffs (write these down)
              </p>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="opacity-90 mb-1">Main water shutoff location:</p>
                  <div className="h-7 border-b border-white/40" />
                </div>
                <div>
                  <p className="opacity-90 mb-1">Gas shutoff location:</p>
                  <div className="h-7 border-b border-white/40" />
                </div>
                <div>
                  <p className="opacity-90 mb-1">Breaker box location:</p>
                  <div className="h-7 border-b border-white/40" />
                </div>
                <div>
                  <p className="opacity-90 mb-1">Landlord emergency line:</p>
                  <div className="h-7 border-b border-white/40" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Pro tips */}
        <section
          className="rounded-2xl p-6"
          style={{ backgroundColor: colors.ivory, border: `2px solid ${colors.gold}` }}
        >
          <h3 className="text-lg font-bold mb-3" style={{ color: colors.charcoal }}>
            A Few Pro Tips
          </h3>
          <ul className="space-y-2 text-sm" style={{ color: colors.charcoal }}>
            <li className="flex gap-3">
              <span className="font-bold" style={{ color: colors.terracotta }}>
                •
              </span>
              <span>
                <strong>Document everything.</strong> Take a photo before and after any repair.
                Save text-message threads with your landlord.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold" style={{ color: colors.terracotta }}>
                •
              </span>
              <span>
                <strong>Never withhold rent</strong> as a tactic for unresolved repairs without
                speaking to a tenant attorney first — it can hurt you legally.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold" style={{ color: colors.terracotta }}>
                •
              </span>
              <span>
                <strong>Renter&apos;s insurance is worth it.</strong> Typically $10–20/month and
                covers your stuff if there&apos;s a fire, theft, or water damage.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold" style={{ color: colors.terracotta }}>
                •
              </span>
              <span>
                <strong>Build a relationship with your landlord.</strong> Polite, prompt
                communication makes a huge difference when you need something fixed.
              </span>
            </li>
          </ul>
        </section>
      </div>
    </PrintableSheet>
  );
};

export default MaintenanceGuide;
